/**
 * Admin Applications page (Iter 9.29 rebuild).
 * Single feed of waitlist + host-application submissions with full applicant
 * detail drawer and a clean status workflow:
 *   waitlist → dashboard_pending → activated → deactivated → archived
 *
 * Surface: /admin/product/applications (mounted via the new Product hub).
 */
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Mail, Phone, MapPin, Loader2, X, Search,
  Instagram, Building2, Calendar, ExternalLink, CheckCircle2,
  Clock, Archive, BadgeCheck, Pause, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import AdminFieldInfo from "@/components/admin/AdminFieldInfo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { id: "all", label: "All", count: 0 },
  { id: "waitlist", label: "Waitlist", icon: Clock, color: "bg-amber-100 text-amber-700" },
  { id: "dashboard_pending", label: "Dashboard pending", icon: RefreshCw, color: "bg-blue-100 text-blue-700" },
  { id: "activated", label: "Activated", icon: BadgeCheck, color: "bg-emerald-100 text-emerald-700" },
  { id: "deactivated", label: "Deactivated", icon: Pause, color: "bg-gray-200 text-gray-700" },
  { id: "archived", label: "Archived", icon: Archive, color: "bg-rose-100 text-rose-700" },
];

const headers = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("session_token")}`,
});

export default function AdminApplicationsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [openRow, setOpenRow] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [draftStatus, setDraftStatus] = useState("");
  const [draftNotes, setDraftNotes] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/applications`, { headers: headers() });
      if (!r.ok) throw new Error(await r.text());
      const d = await r.json();
      setRows(d.applications || []);
    } catch (e) {
      toast.error("Could not load applications: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const lc = search.toLowerCase();
    return rows.filter(r => {
      if (filter !== "all" && r.status !== filter) return false;
      if (!lc) return true;
      const hay = `${r.name || ""} ${r.email || ""} ${r.phone || ""} ${r.city || ""} ${r.company || ""} ${(r.service_categories || []).join(" ")}`.toLowerCase();
      return hay.includes(lc);
    });
  }, [rows, search, filter]);

  const counts = useMemo(() => {
    const c = { all: rows.length };
    rows.forEach(r => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  const openDetail = (row) => {
    setOpenRow(row);
    setDraftStatus(row.status);
    setDraftNotes(row.admin_notes || "");
  };

  const saveStatus = async () => {
    if (!openRow) return;
    setUpdating(true);
    try {
      const r = await fetch(`${API}/admin/applications/${openRow.id}`, {
        method: "PATCH",
        headers: headers(),
        body: JSON.stringify({ status: draftStatus, admin_notes: draftNotes }),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success(`Status updated to ${draftStatus}`);
      setOpenRow(null);
      load();
    } catch (e) {
      toast.error("Update failed: " + e.message);
    } finally {
      setUpdating(false);
    }
  };

  const statusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(o => o.id === status) || STATUS_OPTIONS[1];
    const Icon = opt.icon || Clock;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${opt.color || ""}`}>
        <Icon className="w-3 h-3" /> {opt.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-applications-page">
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sticky top-0 z-20">
        <div className="max-w-[1700px] mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => navigate("/admin/product")} data-testid="apps-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <Mail className="w-5 h-5 text-indigo-600" />
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold">Applications</h1>
              <AdminFieldInfo
                description="Unified feed of every host onboarding submission. Includes both early-access waitlist popups and full subscriber intakes."
                syncs_to={[
                  "/me Hosting section (when activated → host dashboards become visible)",
                  "Beehiiv (Provider Waitlist segment)",
                  "Host Signup smart-route resolution",
                ]}
                source="Mongo: waitlist + host_applications collections"
                testid="apps-header"
              />
              <span className="text-xs text-gray-500">{counts.all} total</span>
            </div>
          </div>
          <button onClick={load} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="apps-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="max-w-[1700px] mx-auto p-4 sm:p-6">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search by name, email, city, category…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="apps-search"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {STATUS_OPTIONS.map(s => (
              <Button
                key={s.id}
                size="sm"
                variant={filter === s.id ? "default" : "outline"}
                onClick={() => setFilter(s.id)}
                className="text-xs h-8"
                data-testid={`apps-filter-${s.id}`}
              >
                {s.label}
                <span className="ml-1 text-[10px] opacity-70">{counts[s.id] || 0}</span>
              </Button>
            ))}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-20 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading applications...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-400 text-sm bg-white rounded-xl border border-gray-200">
            No applications match. Try clearing filters.
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-600">Name</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-600">Contact</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-600">Location</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-600">Categories</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-600">Source</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-600">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-600">Submitted</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-600 w-20">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(row => (
                    <tr key={row.id} className="hover:bg-gray-50" data-testid={`apps-row-${row.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{row.name || "(no name)"}</div>
                        {row.company && <div className="text-[11px] text-gray-500">{row.company}</div>}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        <div>{row.email}</div>
                        {row.phone && <div className="text-gray-400">{row.phone}</div>}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">{row.city || "-"}</td>
                      <td className="px-3 py-2 text-xs">
                        <div className="flex flex-wrap gap-1">
                          {(row.service_categories || []).slice(0, 3).map(c => (
                            <span key={c} className="px-1.5 py-0.5 rounded bg-gray-100 capitalize">{c}</span>
                          ))}
                          {(row.service_categories || []).length > 3 && (
                            <span className="text-gray-400">+{row.service_categories.length - 3}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-[11px] text-gray-500 capitalize">{row.source}</td>
                      <td className="px-3 py-2">{statusBadge(row.status)}</td>
                      <td className="px-3 py-2 text-[11px] text-gray-500">
                        {row.submitted_at ? new Date(row.submitted_at).toLocaleDateString() : "-"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDetail(row)}
                          className="h-7 text-xs"
                          data-testid={`apps-open-${row.id}`}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Detail drawer */}
      {openRow && (
        <div className="fixed inset-0 bg-black/40 z-30" onClick={() => setOpenRow(null)}>
          <div
            className="absolute right-0 top-0 bottom-0 w-full sm:w-[520px] bg-white shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="apps-detail-drawer"
          >
            <div className="border-b border-gray-200 px-5 py-3 flex items-center justify-between">
              <div>
                <h2 className="font-semibold">{openRow.name || "(no name)"}</h2>
                <p className="text-[11px] text-gray-500">{statusBadge(openRow.status)} · {openRow.type}</p>
              </div>
              <button onClick={() => setOpenRow(null)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4 text-sm">
              <Field label="Email" value={openRow.email} icon={Mail} />
              <Field label="Phone" value={openRow.phone} icon={Phone} />
              <Field label="Instagram" value={openRow.instagram} icon={Instagram} />
              <Field label="City" value={openRow.city} icon={MapPin} />
              <Field label="Company / Org" value={openRow.company} icon={Building2} />
              <Field label="Submitted at" value={openRow.submitted_at ? new Date(openRow.submitted_at).toLocaleString() : "-"} icon={Calendar} />
              <Field label="Source page" value={openRow.source} />
              <Field label="Advocate code" value={openRow.advocate_code} />
              <Field label="Service categories" value={(openRow.service_categories || []).join(", ") || "-"} />
              {openRow.other_service_description && (
                <Field label="Other service notes" value={openRow.other_service_description} />
              )}
              {openRow.tagline && <Field label="Tagline" value={openRow.tagline} />}
              {openRow.intro && <Field label="Intro" value={openRow.intro} />}
              {openRow.smart_route_result && (
                <div>
                  <p className="text-[10px] uppercase font-semibold text-gray-500 mb-1">Smart-route result</p>
                  <pre className="text-[11px] bg-gray-50 p-2 rounded border border-gray-200 overflow-x-auto">
                    {JSON.stringify(openRow.smart_route_result, null, 2)}
                  </pre>
                </div>
              )}
              {openRow.event_id && (
                <div>
                  <p className="text-[10px] uppercase font-semibold text-gray-500 mb-1">Linked dashboard</p>
                  <a
                    href={`/host/v2/${openRow.event_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1"
                  >
                    /host/v2/{openRow.event_id} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
              <div className="pt-3 border-t border-gray-200">
                <label className="text-[10px] uppercase font-semibold text-gray-500 mb-1 block">
                  Status
                  <AdminFieldInfo
                    description="Application lifecycle state. Drives whether the user sees a 'You're on the waitlist' badge, a 'Dashboard pending' state, or active host dashboards on /me."
                    syncs_to={["/me Hosting section state", "Beehiiv tag", "Smart-route resolution"]}
                    source="waitlist.status / host_applications.status"
                    testid="apps-status"
                  />
                </label>
                <select
                  value={draftStatus}
                  onChange={(e) => setDraftStatus(e.target.value)}
                  className="w-full h-9 text-sm border border-gray-200 rounded px-2"
                  data-testid="apps-status-select"
                >
                  {STATUS_OPTIONS.filter(s => s.id !== "all").map(s => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase font-semibold text-gray-500 mb-1 block">Admin notes</label>
                <Textarea
                  value={draftNotes}
                  onChange={(e) => setDraftNotes(e.target.value)}
                  rows={4}
                  className="text-xs"
                  placeholder="Internal notes (not visible to applicant)"
                  data-testid="apps-notes-textarea"
                />
              </div>
            </div>
            <div className="border-t border-gray-200 px-5 py-3 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setOpenRow(null)}>Cancel</Button>
              <Button onClick={saveStatus} disabled={updating} data-testid="apps-save-status">
                {updating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Save status
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, icon: Icon }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2">
      {Icon && <Icon className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase font-semibold text-gray-500">{label}</p>
        <p className="text-sm text-gray-800 break-words">{value}</p>
      </div>
    </div>
  );
}
